import React, { useRef } from 'react'
import ReactDOM from 'react-dom'
import srtParser2 from 'srt-parser-2'
import classnames from 'classnames'
import toastr from 'toastr'
import Tooltip from '@SiteComponents/Tooltip'
import useTooltipDelay from '@Utils/UseTooltipDelay'
import AddScript from '@Images/project_setup_form_add_script.svg'

const MAX_SCRIPT_SIZE = 2097152 // Bytes = 2MB
const TOOLTIP_OFFSET = 13 // px
const TOOLTIP_WIDTH = 234 // px from SetupProjectPopup__tooltip-script in SetupProjectPopup.scss

import './AddScriptButton.scss'

interface AddScriptButtonProps {
  readonly cannotImportScript: boolean
  readonly fileRef: React.RefObject<HTMLInputElement>
  readonly isForTranslation?: boolean
  resetScriptUpload(): void
  setScriptTextAndFileName(newScriptFileName: string | null, newScriptText: string): void
  setHasSrt?(newHasSrt: boolean): void
  openFileManager(): void
}

const AddScriptButton: React.FC<AddScriptButtonProps> = props => {
  const {
    cannotImportScript,
    fileRef,
    isForTranslation,
  } = props

  const scriptButtonRef = useRef<HTMLDivElement | null>(null)
  const srtParser = new srtParser2()

  const toolTipDelayHookScript = useTooltipDelay()

  const onMouseEnterScript = () => {
    toolTipDelayHookScript.triggerShowTooltip()
  }

  const onMouseLeaveScript = () => {
    toolTipDelayHookScript.cancelTooltip()
  }

  const handleImportClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (cannotImportScript) { return }

    const files = (e.target as HTMLInputElement).files
    if (!files) { return }

    handleFileImport(Array.from(files))
  }

  const handleFileImport = (files: File[]) => {
    if (files.length === 0) { return }

    if (files[0].size > MAX_SCRIPT_SIZE) {
      toastr.error('Your file is too large (> 2MB).', 'Error')
      props.resetScriptUpload()
      return
    }

    const fileReader = new FileReader()
    fileReader.onload = () => {
      const newScriptText = fileReader.result?.toString()

      if (!newScriptText) {
        toastr.error('Your file cannot be read.', 'Error')
        props.resetScriptUpload()
        return
      }

      if (newScriptText.trim().length === 0) {
        toastr.error('Your file is empty.', 'Error')
        props.resetScriptUpload()
        return
      }

      changeScriptTextAndFileName(newScriptText, files[0].name)
    }

    fileReader.readAsText(files[0])
  }

  const changeScriptTextAndFileName = (newText: string, newFileName: string) => {
    const parsedText = srtParser.fromSrt(newText)
    const newHasSrt = parsedText.length > 0
    if (isForTranslation) {
      if (!newHasSrt) {
        toastr.error('Your file must be in SRT format', 'Error')
        props.resetScriptUpload()
      } else {
        props.setScriptTextAndFileName(srtParser.toSrt(parsedText), newFileName)
      }
    } else {
      if (!!props.setHasSrt) {
        props.setHasSrt(newHasSrt)
      }
      props.setScriptTextAndFileName(newHasSrt ? srtParser.toSrt(parsedText) : newText.replace(/\s+/g,' ').trim(), newFileName)
    }
  }

  const renderTooltipScript = () => {
    const textImport = (
      isForTranslation ? (
        <div>
          Upload a subtitle (SRT) file to enhance the translation quality. Importing an SRT file can also help reduce credit usage.
        </div>
      ) : (
        <div>
          Upload a subtitle (SRT) file or a{' '}
          <span className="AddScriptButton__tooltip-emphasize">clean script</span>
          {' '}(txt) to enhance the transcription quality. Importing an SRT file can also help reduce credit usage.
        </div>
      )
    )

    const textNoImport = (
      isForTranslation ? (
        <div>
          The option to upload a subtitle (SRT) file is available when ordering the subtitles separately for each project from the dashboard
        </div>
      ) : (
        <div>
          The option to upload a subtitle (SRT) file or a script is available when ordering the subtitles separately for each project from the dashboard
        </div>
      )
    )

    const scriptButtonBounds = scriptButtonRef.current ? scriptButtonRef.current.getBoundingClientRect() : { x: 0, y: 0, width: 0, height: 0 }
    const tooltipY = scriptButtonBounds.y + scriptButtonBounds.height + TOOLTIP_OFFSET
    const tooltipX = scriptButtonBounds.x  - 0.5 * TOOLTIP_WIDTH + 0.5 * scriptButtonBounds.width
    const tooltipTitle = isForTranslation ? 'Import an SRT' : 'Import an SRT or script'

    const style = {
      top: `${tooltipY}px`,
      left: `${tooltipX}px`
    }

    return ReactDOM.createPortal(
      (
        <div
          className="SetupProjectPopup__tooltip"
          style={style}
        >
          <Tooltip
            title={tooltipTitle}
            textElement={cannotImportScript ? textNoImport : textImport}
          />
        </div>
      ),
      document.getElementById('site-container')!
    )
  }

  return (
    <div className="AddScriptButton">
      <div
        className={classnames(
          'SetupProjectPopup__button-add',
          { 'SetupProjectPopup__button-add-disabled': cannotImportScript }
        )}
        onMouseEnter={onMouseEnterScript}
        onMouseLeave={onMouseLeaveScript}
        onClick={props.openFileManager}
        ref={scriptButtonRef}
      >
        <AddScript />
        <input
          ref={fileRef}
          type="file"
          className="AddScriptButton__file"
          onChange={handleImportClick}
        />
      </div>
      {toolTipDelayHookScript.showTooltip && renderTooltipScript()}
    </div>
  )
}

export default AddScriptButton
